import { SimplifiedUserRole } from '@platform/client/schemas';
import { Modal, } from '@platform/components';
import { useState } from 'react';
import {
	ErrorConfirmationContent,
	SubmittingContent,
	SuccessConfirmationContent,
} from './ConfirmationContent';
import { OtherUpdateContent } from './OtherUpdateContent';
import { ProjectHub } from '@platform/client';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'wouter';
import { useForm, SubmitHandler } from 'react-hook-form';

export enum PlanUpdateModalStage {
	Different_Update = 'different-update',
	NonSuccess_Update_Confirmed = 'nonsuccess-update-confirmed',
	Submitting = 'submitting',
	Error = 'error',
}

export type PlanUpdateFormData = {
	projectId: number;
	notes: string | null;
};

export const PlanUpdateModalProjectPlan = (props: {
	projectId: number;
	isOpen: boolean;
	onClose: () => void;
	userType: SimplifiedUserRole;
	isTeam: boolean;
}) => {
	const { assignmentDiscoveryId } = useParams<{ assignmentDiscoveryId: string }>();
	const [modalStage, setModalStage] = useState<PlanUpdateModalStage>(
		PlanUpdateModalStage.Different_Update,
	);

	const { handleSubmit, control, formState } = useForm<PlanUpdateFormData>({
		defaultValues: {
			projectId: props.projectId,
		},
	});

	const closeModal = () => {
		setModalStage(PlanUpdateModalStage.Different_Update);
		props.onClose();
	};

	const queryClient = useQueryClient();
	const postPlanUpdateMutator = ProjectHub.usePostPlanUpdate({
		mutation: {
			onMutate: () => {
				setModalStage(PlanUpdateModalStage.Submitting);
			},
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: ProjectHub.getGetProjectHubDataQueryKey({
						assignment_discovery_id: parseInt(assignmentDiscoveryId),
					}),
				});
				queryClient.invalidateQueries({
					queryKey: ProjectHub.getGetTeamProjectHubDataQueryKey({
						project_id: props.projectId,
					}),
				});
				setModalStage(PlanUpdateModalStage.NonSuccess_Update_Confirmed);
			},
			onError: () => {
				setModalStage(PlanUpdateModalStage.Error);
			},
		},
	});

	const onSubmit: SubmitHandler<PlanUpdateFormData> = (data) => {
		postPlanUpdateMutator.mutate({
			data: {
				projectId: props.projectId,
				outcome: 'need_help',
				notes: data.notes ?? null,
				isTeam: props.isTeam,
			},
		});
	};

	return (
		<Modal
			isOpen={props.isOpen}
			headerDivision='divider'
			onClose={closeModal}
			title='Planning update'
			variant='full-screen'
			primaryCTA={{
				text: modalStage === PlanUpdateModalStage.Different_Update ? 'Submit' : 'Close',
				callback: () => {
					if (modalStage === PlanUpdateModalStage.Different_Update) {
						handleSubmit(onSubmit)();
					} else {
						closeModal();
					}
				}
			}}
			secondaryCTA={
				modalStage === PlanUpdateModalStage.Different_Update ? {
					text: 'Go back',
					callback: () => closeModal(),
				} : undefined}
		>
			{modalStage === PlanUpdateModalStage.Different_Update && (
				<OtherUpdateContent control={control} formState={formState} name='notes' />
			)}
			{modalStage === PlanUpdateModalStage.Submitting && <SubmittingContent />}
			{modalStage === PlanUpdateModalStage.NonSuccess_Update_Confirmed && (
				<SuccessConfirmationContent>
					Thanks for sharing your update. It helps us better support your project and track your
					progress.
				</SuccessConfirmationContent>
			)}
			{modalStage === PlanUpdateModalStage.Error && <ErrorConfirmationContent />}
		</Modal>
	);
};
